import React from 'react';
import { Text } from '@atlaskit/primitives/compiled/text';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import type { Status } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import type { EdgeId, ItemReference, ListEdgeDetails } from './ListEdgeDetails.tsx';
import RulesIndicator from './RulesIndicator.tsx';
import ValidationIndicator from './ValidationIndicator.tsx';

const globalTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.global-transition-label',
	defaultMessage: 'Transition from any status to {parentName}.',
	description: 'Label for global transition',
});

const loopedTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.looped-transition-label',
	defaultMessage: '{transitionName} transition - move issue from {parentName} back to itself.',
	description: 'Label for looped transition',
});

const globalLoopedTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation-list-edge.global-looped-transition-label',
	defaultMessage: '{transitionName} transition - move issue from any status back to itself.',
	description: 'Label for global looped transition',
});

const initialTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.initial-transition-label',
	defaultMessage: '{transitionName} transition - move issue to {targetName}.',
	description: 'Label for initial transition',
});

const directedTransitionLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.directed-transition-label',
	defaultMessage: '{transitionName} transition - move issue from {sourceNames} to {targetName}.',
	description: 'Label for directed transition',
});

const loopedTransitionLabelIssueTermRefresh = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.looped-transition-label-issue-term-refresh',
	defaultMessage: '{transitionName} transition - move work item from {parentName} back to itself.',
	description: 'Label for looped transition',
});

const globalLoopedTransitionLabelIssueTermRefresh = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation-list-edge.global-looped-transition-label-issue-term-refresh',
	defaultMessage: '{transitionName} transition - move work item from any status back to itself.',
	description: 'Label for global looped transition',
});

const initialTransitionLabelIssueTermRefresh = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.initial-transition-label-issue-term-refresh',
	defaultMessage: '{transitionName} transition - move work item to {targetName}.',
	description: 'Label for initial transition',
});

const directedTransitionLabelIssueTermRefresh = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-edge.directed-transition-label-issue-term-refresh',
	defaultMessage:
		'{transitionName} transition - move work item from {sourceNames} to {targetName}.',
	description: 'Label for directed transition',
});

type Props = {
	edge: ListEdgeDetails;
	statuses: Status[];
	selectedItem?: ItemReference;
	isImmutable?: boolean;
	describedBy?: string;
	onFocus?: (id: EdgeId) => void;
	onSelect?: (item: ItemReference | undefined) => void;
};

const ListEdge = ({
	edge,
	statuses,
	isImmutable = false,
	describedBy,
	selectedItem,
	onSelect,
	onFocus,
}: Props): React.JSX.Element => {
	const isSelected =
		!!selectedItem && selectedItem.type === EDGE_TYPE && selectedItem.id === edge.id;

	const handleClick = () => {
		onSelect?.({ type: 'edge', id: edge.id });
	};

	const handleFocus = () => {
		onFocus?.(edge.id);
	};

	const content = (
		<>
			<EdgeLabel edge={edge} statuses={statuses} /> <RulesIndicator rulesCount={edge.rulesCount} />{' '}
			<ValidationIndicator validationFlag={edge.validationFlag} />
		</>
	);

	if (isImmutable) {
		return <div aria-current={isSelected}>{content}</div>;
	}

	return (
		// We are using a primitive here to support accessible navigation of complex diagram,
		// this will never be visible to the user
		// eslint-disable-next-line @atlaskit/design-system/no-html-button
		<button
			type="button"
			aria-current={isSelected}
			onClick={handleClick}
			onFocus={handleFocus}
			aria-describedby={describedBy}
		>
			{content}
		</button>
	);
};

const EdgeLabel = ({
	edge: { type, transition, parentId },
	statuses,
}: {
	edge: ListEdgeDetails;
	statuses: Status[];
}) => {
	const { formatMessage, formatList } = useIntl();

	const parentStatus = statuses.find((status) => status.id === parentId);

	if (type === 'GLOBAL_LOOPED') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? globalLoopedTransitionLabelIssueTermRefresh
						: globalLoopedTransitionLabel,
					{
						transitionName: transition.name,
					},
				)}
			</Text>
		);
	}

	if (type === 'GLOBAL') {
		return (
			<Text>
				{formatMessage(globalTransitionLabel, {
					parentName: parentStatus?.name,
				})}
			</Text>
		);
	}

	if (type === 'LOOPED') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? loopedTransitionLabelIssueTermRefresh
						: loopedTransitionLabel,
					{
						transitionName: transition.name,
						parentName: parentStatus?.name,
					},
				)}
			</Text>
		);
	}

	const target = statuses.find((status) => status.id === transition.toRef);

	if (type === 'INITIAL') {
		return (
			<Text>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? initialTransitionLabelIssueTermRefresh
						: initialTransitionLabel,
					{
						transitionName: transition.name,
						targetName: target?.name,
					},
				)}
			</Text>
		);
	}

	const sourceIds = transition.links.map((source) => source.fromRef);

	const otherSourcesNames = statuses
		.filter((status) => status.id !== parentId && sourceIds.includes(status.id))
		.map((status) => status.name);

	return (
		<Text>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? directedTransitionLabelIssueTermRefresh
					: directedTransitionLabel,
				{
					transitionName: transition.name,
					sourceNames: formatList([parentStatus?.name, ...otherSourcesNames]),
					targetName: target?.name,
				},
			)}
		</Text>
	);
};

export default ListEdge;
